import {
    Button,
    Image,
    Modal,
    Space,
    Card,
    Form,
    Input,
    notification,
    Typography,
    Divider,
    Tooltip,
    Row,
    Col
} from "antd";
import {QuestionStats} from "./QuestionStats";
import {useEffect, useRef, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import ReactGA from "react-ga";
import {Trans, useTranslation} from "react-i18next";
import api from "../../api";
import {InfoCircleOutlined, ArrowRightOutlined, StarOutlined, StarFilled} from '@ant-design/icons';

const { Title, Paragraph } = Typography;

function QuestionItem({currentQuestion, handleNext, value, noSubmit, quick_view}) {
    const [selected, setSelected] = useState(value)
    const formRef = useRef();
    const dispatch = useDispatch();
    const {t} = useTranslation();
    const favorites = useSelector(state => state.favorites || []);
    const isFavorite = favorites.includes(currentQuestion.id);

    const toggleFavorite = (e) => {
        e.stopPropagation();
        dispatch({
            type: 'TOGGLE_FAVORITE',
            payload: currentQuestion.id
        });
    }
    
    const handleShowDesc = () => {
        ReactGA.event({
            category: 'Question',
            action: 'Description',
            label: 'open'
        })

        Modal.info({
            title: currentQuestion.description.desc_name,
            width: 600,
            content: (
                <div style={{ marginTop: 20 }}>
                    {currentQuestion.description.desc_image && (
                        <div style={{ textAlign: 'center', marginBottom: 15 }}>
                            <Image 
                                src={process.env.REACT_APP_API_ENDPOINT + currentQuestion.description.desc_image}
                                alt={currentQuestion.description.desc_name}
                                style={{ borderRadius: 8, maxWidth: '100%' }}
                            />
                        </div>
                    )}
                    <Paragraph style={{ fontSize: 16, lineHeight: '1.6' }}>
                        {currentQuestion.description.desc_text}
                    </Paragraph>
                    <Divider />
                    <Button onClick={handleShowNewDesc} type="dashed" block>{t('questions.suggest_description')}</Button>
                </div>
            ),
        })
    }

    const handleShowNewDesc = () => {
        Modal.confirm({
            title: t('questions.write_armenian_letters'),
            onOk: (close) => {
                formRef.current.submit();
                close()
            },
            content: (
                <Form onFinish={handleSaveNewDef} ref={formRef} layout="vertical">
                    <Form.Item label={t('comments.name')} name="title" extra={t('questions.dont_forget_nation')}>
                        <Input placeholder={t('comments.placeholder_name')} />
                    </Form.Item>
                    <Form.Item 
                        label={t('questions.description_label')} 
                        name="description" 
                        rules={[{ message: t('questions.field_required'), required: true }]}
                    >
                        <Input.TextArea rows={4} placeholder={t('comments.placeholder_content')} />
                    </Form.Item>
                </Form>
            )
        })
    }

    const handleSaveNewDef = (formData) => {
        api.API.post(api.Question + `/${currentQuestion.id}/new-definition`, formData).then(() => {
            notification.info({
                description: t('questions.suggestion_success')
            })
        })
    }

    let isSelected = (selected === 0 || selected);
    
    useEffect(() => {
        setSelected(value)
    }, [value])

    const handleSelect = (key) => {
        if (isSelected) return;
        if (quick_view) {
            handleNext(key)
            return;
        }
        setSelected(key)
    }

    const handleConfirm = () => {
        setSelected(false)
        handleNext(selected)
    }

    return (
        <div className="question-item-fancy">
            <Card 
                bordered={false} 
                style={{ background: '#fcfcfc', borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}
            >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Title level={4} style={{ marginBottom: 20, flex: 1 }}>{currentQuestion.question}</Title>
                    <Tooltip title={isFavorite ? t('questions.remove_favorites') : t('questions.add_favorites')}>
                        <Button 
                            type="text" 
                            icon={isFavorite ? <StarFilled style={{ color: '#fadb14', fontSize: 24 }} /> : <StarOutlined style={{ fontSize: 24 }} />} 
                            onClick={toggleFavorite}
                        />
                    </Tooltip>
                </div>
                
                <div style={{ marginBottom: 20 }}>
                    <QuestionStats currentQuestion={currentQuestion}/>
                </div>

                {currentQuestion.image && (
                    <div className="image-block" style={{ marginBottom: 25 }}>
                        <Image
                            className="image-item"
                            alt={currentQuestion.question}
                            src={process.env.REACT_APP_API_ENDPOINT + currentQuestion.image + `?date=${new Date().getDate()}`}
                            style={{ borderRadius: 12, border: '1px solid #f0f0f0' }}
                        />
                    </div>
                )}

                <div className="variants-container">
                    <Space direction="vertical" style={{ width: '100%' }} size="middle">
                        {currentQuestion.variants.map((variant, key) => {
                            let className = "variant-btn";
                            if (isSelected) {
                                if (key === currentQuestion.answer) className += " correct";
                                else if (key === selected) className += " incorrect";
                            } else if (key === selected) {
                                className += " selected";
                            }

                            return (
                                <div 
                                    key={key} 
                                    onClick={() => handleSelect(key)} 
                                    className={className}
                                >
                                    <span className="variant-index">{key + 1}</span>
                                    <span className="variant-text">{variant.value}</span>
                                </div>
                            );
                        })}
                    </Space>
                </div>

                <div style={{ marginTop: 30, textAlign: 'center' }}>
                    <Row gutter={[12, 12]} justify="center">
                        {currentQuestion.description && isSelected && (
                            <Col xs={24} sm={12} md={12}>
                                <Button 
                                    type="ghost" 
                                    danger 
                                    block
                                    icon={<InfoCircleOutlined />} 
                                    onClick={handleShowDesc}
                                    size="large"
                                    style={{ fontSize: '14px', padding: '0 4px' }}
                                >
                                    <Trans i18nKey="questions.see_description">Դիտել</Trans>
                                </Button>
                            </Col>
                        )}
                        
                        {isSelected && !noSubmit && (
                            <Col xs={currentQuestion.description ? 24 : 24} sm={12} md={12}>
                                <Button 
                                    type="primary" 
                                    onClick={handleConfirm} 
                                    size="large"
                                    block
                                    icon={<ArrowRightOutlined />}
                                    style={{ borderRadius: 8 }}
                                >
                                    <Trans i18nKey="questions.next">Հաջորդը</Trans>
                                </Button>
                            </Col>
                        )}
                    </Row>
                </div>
            </Card>
        </div>
    )
}

export default QuestionItem;
